// Custom hook do zarządzania logiką HydraAIAssistant

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch, reduxActions, store} from 'store';
import {
  axios,
  createThread,
  updateThreadContext,
  addMessage,
  getThreadContent,
  addAttachment,
  removeAttachment,
  cancelProcessing,
  removeLastMessage as removeLastMessageApi,
  getAttachments,
  type ThreadContext,
  type ChatResponse,
  type ChatAttachment
} from 'api';
import { AssistantInfo, Message } from 'components/containers/ChatBase/HydraAIAssistant.types';
import {
  translateI18nTags,
  extractLastTurnId,
  replaceUrlPlaceholders,
  mergeIncrementalContent,
  computeContentHash,
  getBaseUrl,
  arrayBufferToBase64,
  simpleParseMessages
} from 'components/containers/ChatBase/helpers';
import { isWiseAiEnabled } from 'config/wiseAi';

const logError = (message: string, error?: unknown) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.error(message, error);
  }
};

/**
 * Klucz w localStorage dla threadId konkretnego asystenta. Bez tego watki
 * wszystkich asystentow nadpisywaly sie (np. po przejsciu ze sklepu do panelu
 * handlowca uzytkownik widzial wczesniejszy watek `wiseb2b`).
 */
const threadStorageKey = (assistantName: string): string =>
  assistantName ? `chatThreadId:${assistantName}` : 'chatThreadId';

interface UseHydraAIAssistantProps {
  availableAssistants: AssistantInfo[];
  urlParameters: Record<string, string>;
  apiBaseUrl?: string;
}

export const useHydraAIAssistant = ({
  availableAssistants = [],
  urlParameters = {},
  apiBaseUrl
}: UseHydraAIAssistantProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { profile } = useSelector((state) => state.auth);

  const chatApiBaseUrl = apiBaseUrl || process.env.REACT_APP_CHAT_API_BASE_URL || 'http://localhost:5427';

  // State
  const [userMessage, setUserMessage] = useState('');
  const [threadId, setThreadId] = useState<string>('');
  const [conversationContent, setConversationContent] = useState('');
  const [selectedAssistant, setSelectedAssistant] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [fileNameToAttachmentId, setFileNameToAttachmentId] = useState<Record<string, string>>({});
  const [filteredAssistants, setFilteredAssistants] = useState<AssistantInfo[]>([]);
  const [lastReceivedTurnId, setLastReceivedTurnId] = useState<string | null>(null);
  const [isIframeFullscreen, setIsIframeFullscreen] = useState(false);
  // Strukturalne wiadomosci z backendu WiseAi (tool calls, tool results, asystent).
  // Wystawiane przez adapter `wiseAiEndpoints` w trybie REACT_APP_WISE_AI=true.
  // Pozwala na role-based renderowanie zamiast surowego markdown blob.
  const [structuredMessages, setStructuredMessages] = useState<
    NonNullable<ChatResponse['structuredMessages']>
  >([]);

  // Refs
  const chatDivRef = useRef<HTMLDivElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const assistantDropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const processingAbortControllerRef = useRef<AbortController | null>(null);
  const conversationContentRef = useRef<string>('');
  const lastReceivedTurnIdRef = useRef<string | null>(null);
  // Hash tresci watku z odpowiedzi backendu — uzywany w nastepnym pollingu zeby
  // backend mogl pominac SELECT na chat_messages gdy nic sie nie zmienilo.
  // Bez tego optymalizator no-change na backendzie nigdy nie zadziala bo frontend
  // wysyla wlasny hash (computeContentHash) ktory nie ma zwiazku z backendowym sha256.
  const lastServerContentHashRef = useRef<string | null>(null);
  const isThreadLoadingRef = useRef<boolean>(false);
  const hasThreadBeenLoadedRef = useRef<boolean>(false);
  const fileNameToAttachmentIdRef = useRef<Record<string, string>>({});
  const attachedFilesRef = useRef<File[]>([]);

  const dispatch = useDispatch()

  // Computed properties
  const isDeveloperMode = urlParameters?.devel === '1' || process.env.REACT_APP_DEVELOPER_MODE || process.env.NODE_ENV === 'development';
  const showAssistantDropdown = filteredAssistants.length > 1;
  const attachedFilesCount = attachedFiles.length;

  // Synchronizuj refs ze state
  useEffect(() => {
    conversationContentRef.current = conversationContent;
  }, [conversationContent]);

  useEffect(() => {
    lastReceivedTurnIdRef.current = lastReceivedTurnId;
  }, [lastReceivedTurnId]);

  useEffect(() => {
    fileNameToAttachmentIdRef.current = fileNameToAttachmentId;
  }, [fileNameToAttachmentId]);

  useEffect(() => {
    attachedFilesRef.current = attachedFiles;
  }, [attachedFiles]);

  // Helper functions
  // Optimized smooth scrolling for streaming experience
  const scrollToBottom = useCallback(() => {
    if (chatDivRef.current) {
      // Use immediate scroll for better responsiveness during streaming
      const element = chatDivRef.current;
      // Scroll immediately for instant feedback
      element.scrollTop = element.scrollHeight;
      // Then use RAF for smooth animation if needed
      requestAnimationFrame(() => {
        if (element) {
          element.scrollTop = element.scrollHeight;
        }
      });
    }
  }, []);

  const getCurrentThreadContext = useCallback((): ThreadContext => {
    const currentPage = location.pathname;
    const userLanguage = i18n.language.split('-')[0] || 'pl';

    return {
      userName: profile ? `${profile.first_name} ${profile.last_name}`.trim() : '',
      currentModuleName: '',
      currentPage: '',
      fullUrl: currentPage,
      userLanguage,
      // Wybrany asystent — adapter WiseAi przekaze go jako `selectedAssistantName`
      // do POST /new-thread (np. `trader` w panelu handlowca, `wiseb2b` w sklepie).
      selectedAssistantName: selectedAssistant
    };
  }, [location.pathname, i18n.language, profile, selectedAssistant]);

  /**
   * Buduje `hiddenContext` z biezacego URL-a frontendu — propagowany do asystenta
   * jako ulotny `system` message (tylko w tej turze, NIE pokazywany userowi).
   *
   * Pozwala asystentowi rozumiec pytania z zaimkami ("jego oferty", "tej pozycji")
   * gdy handlowiec jest juz na stronie konkretnego klienta/oferty/zadania.
   *
   * Rozpoznawane wzorce URL (Panel Handlowca):
   *  - /trader/clients/:id                         -> clientId + sekcja
   *  - /trader/clients/:id/edit                    -> clientId + sekcja=edit-client
   *  - /trader/clients/:id/offers                  -> clientId + sekcja=client-offers
   *  - /trader/clients/:id/offers/:offerId         -> clientId + offerId + sekcja=edit-offer
   *  - /trader/clients/:id/carts                   -> clientId + sekcja=client-carts
   *  - /trader/clients/:id/orders                  -> clientId + sekcja=client-orders
   *  - /trader/clients/:id/products/:productId     -> clientId + productId
   *  - /trader/tasks/:id                           -> taskId + sekcja=edit-task
   *  - /trader/insights/:clientId                  -> clientId + sekcja=insights
   *  - /trader/deals                               -> sekcja=deals-kanban
   *  - /trader/deals/:id                           -> dealId + sekcja=edit-deal
   *
   * Zwraca pusty string gdy URL nie pasuje (asystent dziala bez kontekstu UI).
   */
  const buildHiddenContextFromUrl = useCallback((): string => {
    const path = location.pathname;
    const parts: string[] = [];

    // /trader/clients/:id/...
    const clientMatch = path.match(/\/trader\/clients\/(\d+)(?:\/([^/]+)(?:\/(\d+))?)?/);
    if (clientMatch) {
      const clientId = clientMatch[1];
      const subResource = clientMatch[2];
      const subId = clientMatch[3];
      parts.push(`clientId=${clientId}`);

      if (subResource === 'edit') {
        parts.push('sekcja=edit-client');
      } else if (subResource === 'offers' && subId) {
        parts.push(`offerId=${subId}`, 'sekcja=edit-offer');
      } else if (subResource === 'offers') {
        parts.push('sekcja=client-offers');
      } else if (subResource === 'carts') {
        parts.push('sekcja=client-carts');
      } else if (subResource === 'orders') {
        parts.push('sekcja=client-orders');
      } else if (subResource === 'documents') {
        parts.push('sekcja=client-documents');
      } else if (subResource === 'payments') {
        parts.push('sekcja=client-payments');
      } else if (subResource === 'shopping-lists') {
        parts.push('sekcja=client-shopping-lists');
      } else if (subResource === 'products' && subId) {
        parts.push(`productId=${subId}`, 'sekcja=client-product-card');
      } else if (subResource === 'products') {
        parts.push('sekcja=client-products');
      } else if (!subResource) {
        parts.push('sekcja=client-card');
      }
    }

    // /trader/tasks/:id
    const taskMatch = path.match(/\/trader\/tasks\/(\d+|new)$/);
    if (taskMatch && !clientMatch) {
      if (taskMatch[1] === 'new') {
        parts.push('sekcja=new-task');
      } else {
        parts.push(`taskId=${taskMatch[1]}`, 'sekcja=edit-task');
      }
    }

    // /trader/insights/:clientId
    const insightsMatch = path.match(/\/trader\/insights\/(\d+)$/);
    if (insightsMatch && !clientMatch) {
      parts.push(`clientId=${insightsMatch[1]}`, 'sekcja=insights-client');
    }

    // /trader/products/:productId
    const productMatch = path.match(/\/trader\/products\/(\d+)$/);
    if (productMatch && !clientMatch) {
      parts.push(`productId=${productMatch[1]}`, 'sekcja=product-card');
    }

    // /trader/deals (kanban — bez ID, ale informuje asystenta ze user jest na widoku transakcji)
    const dealsKanbanMatch = path.match(/\/trader\/deals\/?$/);
    if (dealsKanbanMatch) {
      parts.push('sekcja=deals-kanban');
    }

    // /trader/deals/:id (detail transakcji — wlasnie ten przypadek pozwala AI rozumiec
    // "tej transakcji", "ten deal", "tego klienta" w komendach handlowca)
    const dealDetailMatch = path.match(/\/trader\/deals\/(\d+)$/);
    if (dealDetailMatch) {
      parts.push(`dealId=${dealDetailMatch[1]}`, 'sekcja=edit-deal');
    }

    return parts.join(', ');
  }, [location.pathname]);

  // API wrapper functions
  const createChatThread = useCallback(async (threadContext: ThreadContext): Promise<string> => {
    return await createThread({ context: threadContext });
  }, []);

  const updateThreadContextWrapper = useCallback(async (threadId: string, threadContext: ThreadContext): Promise<void> => {
    await updateThreadContext(threadId, { context: threadContext });
  }, []);

  const addChatMessage = useCallback(async (
    threadId: string,
    message: string,
    assistant: string,
    urlParams: Record<string, string>
  ): Promise<void> => {
    const requestBody: {
      message: string;
      urlParameters?: Record<string, string>;
      hiddenContext?: string;
    } = {
      message: message || ''
    };

    if (urlParams && Object.keys(urlParams).length > 0) {
      requestBody.urlParameters = urlParams;
    }

    // Dolacz hiddenContext z URL (clientId, offerId, taskId, sekcja). Asystent dostaje to
    // jako ulotny system message — pomaga interpretowac "tej oferty", "jego klientow" itp.
    const hidden = buildHiddenContextFromUrl();
    if (hidden.length > 0) {
      requestBody.hiddenContext = hidden;
    }

    await addMessage(threadId, assistant, requestBody);
  }, [buildHiddenContextFromUrl]);

  const getChatThreadContent = useCallback(async (
    threadId: string,
    includeDetails: number,
    lastTurnId: string,
    contentHash: string
  ): Promise<ChatResponse> => {
    return await getThreadContent(threadId, includeDetails, lastTurnId, contentHash);
  }, []);

  const cancelChatProcessing = useCallback(async (threadId: string): Promise<void> => {
    await cancelProcessing(threadId);
  }, []);

  const removeLastUserMessage = useCallback(async (threadId: string): Promise<Message | null> => {
    // `removeLastMessageApi` jest udostepniany przez api/assistantApi (load impl dynamicznie),
    // a wiseAi-endpoints deklaruje return type `Promise<unknown>`. Run-time format pasuje do
    // `Message`, ale TS wymaga jawnego rzutowania.
    const result = (await removeLastMessageApi(threadId)) as Message | null | undefined;
    return result ?? null;
  }, []);

  const addChatAttachment = useCallback(async (
    threadId: string,
    fileName: string,
    contentType: string,
    size: number,
    fileContent: ArrayBuffer
  ): Promise<string> => {
    const base64Content = arrayBufferToBase64(fileContent);

    const attachmentId = await addAttachment(threadId, {
      fileName,
      contentType,
      size,
      content: base64Content
    });
    
    if (typeof attachmentId === 'string') {
      return attachmentId.trim().replace(/^"|"$/g, '');
    }
    return attachmentId;
  }, []);

  const removeChatAttachment = useCallback(async (threadId: string, attachmentId: string): Promise<void> => {
    await removeAttachment(threadId, attachmentId);
  }, []);

  const getChatAttachments = useCallback(async (threadId: string): Promise<ChatAttachment[]> => {
    return await getAttachments(threadId);
  }, []);

  // Thread management
  const loadHistory = useCallback(async (threadIdToLoad?: string) => {
    const targetThreadId = threadIdToLoad || threadId;
    if (!targetThreadId) {
      return;
    }

    try {
      const response = await getChatThreadContent(targetThreadId, includeDetails ? 1 : 0, 'none', 'none');
      let content = replaceUrlPlaceholders(
        response.chunk,
        getBaseUrl(),
        chatApiBaseUrl
      );

      content = translateI18nTags(content, t);
      setConversationContent(content);
      conversationContentRef.current = content;
      if (response.structuredMessages) {
        setStructuredMessages(response.structuredMessages);
      }

      const newLastTurnId = extractLastTurnId(content);
      if (newLastTurnId) {
        setLastReceivedTurnId(newLastTurnId);
        lastReceivedTurnIdRef.current = newLastTurnId;
      }

      // Check if thread was lost (empty currentAssistantName indicates thread was lost after server restart)
      // Only show error if there's conversation history (thread had messages before).
      // W trybie WiseAi backend zwraca `currentAssistantName=null` gdy state=completed —
      // to nie oznacza utraty watku, wiec detekcje pomijamy.
      const hasHistory = content && content.trim().length > 0;
      if (
        !isWiseAiEnabled() &&
        hasHistory &&
        (response.currentAssistantName === '' || response.currentAssistantName === null || response.currentAssistantName === undefined)
      ) {
        dispatch(reduxActions.setChatThreadLost(true));
        dispatch(reduxActions.setIsChatHeaderVisible(true));
      } else {
        // Clear thread lost error if assistant name is present or no history exists
        dispatch(reduxActions.setChatThreadLost(false));
      }
    } catch (error) {
      logError('Error loading chat history:', error);
    }
  }, [threadId, includeDetails, chatApiBaseUrl, t, getChatThreadContent]);

  const startNewThread = useCallback(async () => {
    if (isThreadLoadingRef.current) {
      return;
    }

    isThreadLoadingRef.current = true;

    try {
      // Clear attachments
      if (attachedFiles.length > 0) {
        for (const fileName of Object.keys(fileNameToAttachmentId)) {
          const attachmentId = fileNameToAttachmentId[fileName];
          try {
            await removeChatAttachment(threadId || '', attachmentId);
          } catch {
            // Ignore errors
          }
        }
        setAttachedFiles([]);
        setFileNameToAttachmentId({});
      }

      const threadContext = getCurrentThreadContext();
      const newThreadId = await createChatThread(threadContext);
      setThreadId(newThreadId);
      
      if (isInitialized) {
        localStorage.setItem(threadStorageKey(selectedAssistant), newThreadId);
      }

      setConversationContent('');
      conversationContentRef.current = '';
      setLastReceivedTurnId(null);
      lastReceivedTurnIdRef.current = null;
      lastServerContentHashRef.current = null;
      setStructuredMessages([]);
      
      // Clear thread lost error when starting new thread
      dispatch(reduxActions.setChatThreadLost(false));

      // Pokaż chat header ponownie po wyczyszczeniu konwersacji
      dispatch(reduxActions.setIsChatHeaderVisible(true));
    } catch (error) {
      logError('Error creating new chat thread:', error);
    } finally {
      isThreadLoadingRef.current = false;
    }
  }, [
    attachedFiles.length,
    fileNameToAttachmentId,
    threadId,
    isInitialized,
    getCurrentThreadContext,
    createChatThread,
    removeChatAttachment
  ]);

  const updateThreadContextAsync = useCallback(async () => {
    if (!threadId) return;

    try {
      const threadContext = getCurrentThreadContext();
      await updateThreadContextWrapper(threadId, threadContext);
    } catch (error) {
      logError('Error updating chat thread context:', error);
    }
  }, [threadId, getCurrentThreadContext, updateThreadContextWrapper]);

  const sendMessage = useCallback(async (messageOverride?: string) => {
    const messageToProcess = messageOverride !== undefined ? messageOverride : userMessage;
    
    // Allow sending if there's a message OR if there are attached files
    if (!messageToProcess.trim() && attachedFiles.length === 0) {
      return;
    }
    
    // Ukryj chat header po wysłaniu pierwszej wiadomości
    dispatch(reduxActions.setIsChatHeaderVisible(false));
    
    let currentThreadId =
      threadId || localStorage.getItem(threadStorageKey(selectedAssistant)) || '';

    if (!currentThreadId) {
      try {
        const threadContext = getCurrentThreadContext();
        currentThreadId = await createChatThread(threadContext);

        if (!currentThreadId) {
          logError('Failed to create thread');
          return;
        }

        setThreadId(currentThreadId);
        localStorage.setItem(threadStorageKey(selectedAssistant), currentThreadId);
      } catch (error) {
        logError('Error creating thread:', error);
        return;
      }
    }

    const messageToSend = messageToProcess;
    
    // Wyczyść pole input tylko jeśli nie używamy override
    if (messageOverride === undefined) {
      setUserMessage('');
      if (textAreaRef.current) {
        textAreaRef.current.value = '';
      }
    }

    setIsProcessing(true);
    let emptyChunkCount = 0;

    const abortController = new AbortController();
    processingAbortControllerRef.current = abortController;

    try {
      // W trybie WiseAi odswiezanie tokena odbywa sie w tle co 15 min (useEffect ponizej).
      // W trybie Orion zachowujemy historyczne zachowanie — prefetch /users/profile
      // przed kazda wiadomoscia, aby wymusic refresh tokena przez interceptor.
      if (!isWiseAiEnabled()) {
        try {
          await axios.get('/users/profile');
        } catch (error) {
          // Ignoruj — interceptor sam odswiezy token jezeli trzeba.
        }
      }

      const sessionId = store.getState().auth.sessionId;
      const urlParamsWithSession = {
        ...urlParameters,
        ...(sessionId ? { session_id: sessionId } : {})
      };
      
      await addChatMessage(currentThreadId, messageToSend, selectedAssistant, urlParamsWithSession);

      // Wyczyść załączniki po wysłaniu wiadomości
      if (attachedFiles.length > 0) {
        setAttachedFiles([]);
        setFileNameToAttachmentId({});
      }

      // Poll for response
      let pollIteration = 0;
      while (!abortController.signal.aborted) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        pollIteration++;
        
        // Uzywaj `serverContentHash` z poprzedniego pollingu (z backendu),
        // nie wlasnego liczonego z `conversationContent.length + turnId` —
        // wlasny hash NIGDY nie zmatchuje sha256 z backendu, wiec optymalizator
        // no-change backendu (linia 77 GetChatThreadContentService) byl martwy.
        // Pierwszy polling: brak hasha — backend zwroci pelne wiadomosci. Kolejne:
        // hash z poprzedniej odpowiedzi -> backend zwroci pusty `messages: []` gdy nic sie nie zmienilo.
        const currentContentHash = lastServerContentHashRef.current || 'none';

        try {
          const lastTurnIdToUse = lastReceivedTurnIdRef.current || 'none';

          const response = await getChatThreadContent(
            currentThreadId,
            includeDetails ? 1 : 0,
            lastTurnIdToUse,
            currentContentHash
          );

          // Zapisz hash z backendu do uzycia w nastepnej iteracji pollingu.
          if (response.serverContentHash) {
            lastServerContentHashRef.current = response.serverContentHash;
          }

          // WiseAi: updatuj structured messages tylko gdy backend faktycznie
          // zwrocil nowe wiadomosci. Pusta tablica oznacza no-change (backend
          // skrocil response przez contentHash match) — zachowujemy poprzedni
          // stan zeby uniknac "znikniecia" rozmowy w UI podczas pollingu.
          if (response.structuredMessages && response.structuredMessages.length > 0) {
            setStructuredMessages(response.structuredMessages);
          }

          if (response.chunk) {
            emptyChunkCount = 0;

            let processedContent = replaceUrlPlaceholders(
              response.chunk,
              getBaseUrl(),
              chatApiBaseUrl
            );

            processedContent = translateI18nTags(processedContent, t);

            if (response.fromTurnId) {
              setConversationContent((prev) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const merged = mergeIncrementalContent(prev, processedContent, response.fromTurnId!);
                conversationContentRef.current = merged;
                const newLastTurnId = extractLastTurnId(merged);
                if (newLastTurnId) {
                  setLastReceivedTurnId(newLastTurnId);
                  lastReceivedTurnIdRef.current = newLastTurnId;
                }
              return merged;
            });
          } else {
            setConversationContent(processedContent);
            conversationContentRef.current = processedContent;
            const newLastTurnId = extractLastTurnId(processedContent);
            if (newLastTurnId) {
              setLastReceivedTurnId(newLastTurnId);
              lastReceivedTurnIdRef.current = newLastTurnId;
            }
          }

          // Smooth scrolling for better streaming experience
          // Use double RAF for smoother updates during rapid content changes
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              scrollToBottom();
            });
          });
          } else {
            emptyChunkCount++;
          }

          if (response.currentAssistantName && response.currentAssistantName !== selectedAssistant) {
            setSelectedAssistant(response.currentAssistantName);
          }

          // Check if thread was lost (empty currentAssistantName indicates thread was lost after server restart)
          // Only show error if there's conversation history (thread had messages before).
          // W trybie WiseAi backend zwraca `currentAssistantName=null` po completed —
          // to nie oznacza utraty watku, wiec detekcje pomijamy.
          const hasHistory = conversationContentRef.current && conversationContentRef.current.trim().length > 0;
          if (
            !isWiseAiEnabled() &&
            hasHistory &&
            (response.currentAssistantName === '' || response.currentAssistantName === null || response.currentAssistantName === undefined)
          ) {
            dispatch(reduxActions.setChatThreadLost(true));
            dispatch(reduxActions.setIsChatHeaderVisible(true));
          } else {
            // Clear thread lost error if assistant name is present or no history exists
            dispatch(reduxActions.setChatThreadLost(false));
          }

          if (response.isDone) {
            // Final sync: backend zakonczyl przetwarzanie, ale w trakcie pollingu mogly
            // powstac wiadomosci (tool_call_request, tool, finalna odpowiedz asystenta) ktore
            // zostaly utworzone PO ostatnim filtrowanym `lastTurnId`, ale przed `is_done=true`.
            // Robimy jeden pelny fetch (lastTurnId=0) zeby pobrac brakujace wiadomosci
            // i zsynchronizowac UI z baza danych.
            try {
              const finalResponse = await getChatThreadContent(
                currentThreadId,
                includeDetails ? 1 : 0,
                '0',
                '0'
              );
              if (finalResponse.structuredMessages) {
                setStructuredMessages(finalResponse.structuredMessages);
              }
              if (finalResponse.chunk) {
                let finalContent = replaceUrlPlaceholders(finalResponse.chunk, getBaseUrl(), getBaseUrl());
                finalContent = translateI18nTags(finalContent, t);
                setConversationContent(finalContent);
                conversationContentRef.current = finalContent;
                const finalLastTurnId = extractLastTurnId(finalContent);
                if (finalLastTurnId) {
                  setLastReceivedTurnId(finalLastTurnId);
                  lastReceivedTurnIdRef.current = finalLastTurnId;
                }
              }
            } catch (finalError) {
              // Final sync nie jest krytyczny - jesli sie nie uda, polling juz zaktualizowal
              // czesc wiadomosci. Logujemy w konsoli i breakujemy normalnie.
              // eslint-disable-next-line no-console
              console.warn('Chat: final sync failed', finalError);
            }
            break;
          }

          // Bezpiecznik: max 60 pustych pollow zanim przerwiemy (z `500ms` chunked / `1500ms` idle
          // daje ~60-90s czekania). Wczesniej bylo 120 ale przy `100ms/300ms`, teraz ten sam czas
          // przy mniejszej liczbie requestow.
          if (emptyChunkCount > 60) {
            break;
          }

          // Adaptive delay: szybciej gdy LLM streamuje, wolniej gdy czekamy.
          // Zwiekszone z 100/300ms na 500/1500ms zeby zmniejszyc obciazenie backendu
          // dla wielu rownoleglych rozmow (10 userow * 100ms = 100 req/s tylko na asystenta,
          // co przeciaza PHP-FPM i Postgres). Adapter chunkow nadal nadazy nad streamem LLM
          // — typowy chunk to caly tool_call lub kilkadziesiat tokenow assistant content,
          // wiec 500ms latency jest niezauwazalne dla usera.
          //
          // Pomysl na pozniej: zastapic polling przez SSE/long-polling (eliminuje cykl GET),
          // wtedy moglibysmy przejsc na 0 req/s w spoczynku.
          const delay = response.chunk ? 500 : 1500;

          await new Promise((resolve, reject) => {
            const timeoutId = setTimeout(resolve, delay);
            abortController.signal.addEventListener('abort', () => {
              clearTimeout(timeoutId);
              reject(new Error('Aborted'));
            }, { once: true });
          }).catch(() => {
            // Timeout was aborted, break the loop
            return Promise.reject(new Error('Aborted'));
          });
        } catch (error: any) {
          if (abortController.signal.aborted || error?.message === 'Aborted') {
            break;
          }
          logError('Error in polling iteration:', error);
          throw error;
        }
      }
    } catch (error: any) {
      logError('Error sending message:', error);
    } finally {
      setIsProcessing(false);
      processingAbortControllerRef.current = null;
    }
  }, [
    userMessage,
    attachedFiles.length,
    threadId,
    selectedAssistant,
    includeDetails,
    urlParameters,
    chatApiBaseUrl,
    t,
    getCurrentThreadContext,
    createChatThread,
    addChatMessage,
    getChatThreadContent,
    scrollToBottom
  ]);

  const stopProcessing = useCallback(async () => {
    if (processingAbortControllerRef.current) {
      processingAbortControllerRef.current.abort();
    }
    setIsProcessing(false);

    if (threadId) {
      try {
        await cancelChatProcessing(threadId);
      } catch (error) {
        logError('Error cancelling processing:', error);
      }
    }
  }, [threadId, cancelChatProcessing]);

  const removeLastMessage = useCallback(async () => {
    if (!threadId) return;

    try {
      await removeLastUserMessage(threadId);
      await loadHistory();
    } catch (error) {
      logError('Error removing last message:', error);
    }
  }, [threadId, removeLastUserMessage, loadHistory]);

  // Assistant management
  const filterAssistants = useCallback(() => {
    const visibleAssistantsParam = urlParameters?.visible_assistants;
    let filtered = availableAssistants;

    if (visibleAssistantsParam) {
      const visibleNames = visibleAssistantsParam.split(',').map(s => s.trim().toLowerCase());
      filtered = availableAssistants.filter(a => 
        visibleNames.includes(a.name.toLowerCase()) || 
        visibleNames.includes(a.symbol.toLowerCase())
      );
    }

    setFilteredAssistants(filtered);
  }, [availableAssistants, urlParameters]);

  const findAssistantByNameOrSymbol = useCallback((nameOrSymbol: string): AssistantInfo | null => {
    const search = nameOrSymbol.toLowerCase();
    return filteredAssistants.find(
      a => a.name.toLowerCase() === search || a.symbol.toLowerCase() === search
    ) || null;
  }, [filteredAssistants]);

  const selectAssistant = useCallback((assistantName: string) => {
    setSelectedAssistant(assistantName);
    if (isInitialized) {
      localStorage.setItem('selectedAssistant', assistantName);
    }
  }, [isInitialized]);

  const canCurrentAssistantAttachFiles = useCallback((): boolean => {
    const assistant = filteredAssistants.find(a => a.name === selectedAssistant);
    return assistant?.canAttachFiles ?? false;
  }, [filteredAssistants, selectedAssistant]);

  // File management
  const loadAttachments = useCallback(async () => {
    if (!threadId) return;

    try {
      const attachments = await getChatAttachments(threadId);
      const mapping: Record<string, string> = {};
      attachments.forEach(att => {
        mapping[att.fileName] = att.id;
      });
      setFileNameToAttachmentId(mapping);
    } catch (error) {
      logError('Error loading attachments:', error);
    }
  }, [threadId, getChatAttachments]);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles = Array.from(files);

    // Usuń stare pliki - w kolejce ma być tylko najnowszy
    // Użyj ref do przechowania aktualnych wartości przed aktualizacją
    const oldFiles = [...attachedFilesRef.current];
    const oldMapping = { ...fileNameToAttachmentIdRef.current };

    // Zastąp stare pliki nowymi
    setAttachedFiles(newFiles);

    // Wyczyść mapowanie - zostanie wypełnione nowymi poniżej
    setFileNameToAttachmentId({});

    // Usuń stare załączniki z serwera
    if (threadId && oldFiles.length > 0) {
      oldFiles.forEach(oldFile => {
        const attachmentId = oldMapping[oldFile.name];
        if (attachmentId) {
          removeChatAttachment(threadId, attachmentId).catch(error => {
            logError('Error removing old attachment:', error);
          });
        }
      });
    }

    // Dodaj nowe pliki do serwera
    if (threadId && newFiles.length > 0) {
      for (const file of newFiles) {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const attachmentId = await addChatAttachment(
            threadId,
            file.name,
            file.type,
            file.size,
            arrayBuffer
          );

          setFileNameToAttachmentId(prev => ({
            ...prev,
            [file.name]: attachmentId
          }));
        } catch (error) {
          logError('Error uploading attachment:', error);
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [threadId, addChatAttachment, removeChatAttachment]);

  const handleFileRemoved = useCallback(async (fileName: string) => {
    const attachmentId = fileNameToAttachmentId[fileName];
    if (attachmentId && threadId) {
      try {
        await removeChatAttachment(threadId, attachmentId);
      } catch (error) {
        logError('Error removing attachment:', error);
      }
    }

    setFileNameToAttachmentId(prev => {
      const newMapping = { ...prev };
      delete newMapping[fileName];
      return newMapping;
    });
  }, [fileNameToAttachmentId, threadId, removeChatAttachment]);

  // Thread loading
  const loadOrCreateThread = useCallback(async () => {
    if (!isInitialized || !chatApiBaseUrl) {
      return;
    }

    try {
      const storedThreadId = localStorage.getItem(threadStorageKey(selectedAssistant));

      if (storedThreadId) {
        setThreadId(storedThreadId);
        await loadHistory(storedThreadId); // Przekaż threadId bezpośrednio

        // Small delay to let state update - use RAF instead of setTimeout
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

        if (!conversationContentRef.current || conversationContentRef.current.trim() === '') {
          await startNewThread();
        } else {
          await updateThreadContextAsync();
        }
      } else {
        await startNewThread();
      }
    } catch (error) {
      logError('Error in loadOrCreateThread:', error);
    } finally {
      isThreadLoadingRef.current = false;
    }
  }, [isInitialized, chatApiBaseUrl, loadHistory, startNewThread, updateThreadContextAsync]);

  // Event handlers
  const handleToggleFullscreen = useCallback(() => {
    setIsIframeFullscreen((prev) => {
      const newValue = !prev;

      try {
        if (window.parent !== window) {
          window.parent.postMessage(
            {
              type: 'hydra-ai-fullscreen-change',
              isFullscreen: newValue
            },
            '*'
          );
        }
      } catch (error) {
        logError('Could not notify parent about fullscreen change:', error);
      }

      return newValue;
    });
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      // Allow sending if there's a message OR if there are attached files
      if (!isProcessing && (userMessage.trim() || attachedFiles.length > 0)) {
        sendMessage();
      }
    }
  }, [isProcessing, userMessage, attachedFiles.length, sendMessage]);

  // Effects
  useEffect(() => {
    filterAssistants();
  }, [availableAssistants, urlParameters]); // Use direct dependencies instead of callback

  useEffect(() => {
    if (filteredAssistants.length > 0) {
      if (!selectedAssistant || urlParameters?.default_assistant) {
        if (urlParameters?.default_assistant) {
          const foundAssistant = findAssistantByNameOrSymbol(urlParameters.default_assistant);
          if (foundAssistant && filteredAssistants.includes(foundAssistant)) {
            setSelectedAssistant(foundAssistant.name);
          } else {
            const defaultAssistant = filteredAssistants.find((a) => a.isDefault);
            setSelectedAssistant(defaultAssistant?.name || filteredAssistants[0]?.name || '');
          }
        } else if (!selectedAssistant) {
          const defaultAssistant = filteredAssistants.find((a) => a.isDefault);
          setSelectedAssistant(defaultAssistant?.name || filteredAssistants[0]?.name || '');
        }

        if (filteredAssistants.length === 1) {
          setSelectedAssistant(filteredAssistants[0].name);
        }
      }
    }
  }, [filteredAssistants, urlParameters?.default_assistant, selectedAssistant, findAssistantByNameOrSymbol]);

  // Reset stanu watku gdy zmienia sie asystent (np. wejscie do panelu handlowca po
  // wczesniejszej rozmowie w sklepie). Bez tego user widzialby poprzedni watek
  // z innego asystenta.
  const previousAssistantRef = useRef<string>('');
  useEffect(() => {
    if (!selectedAssistant) return;
    if (
      previousAssistantRef.current &&
      previousAssistantRef.current !== selectedAssistant
    ) {
      setThreadId('');
      setConversationContent('');
      conversationContentRef.current = '';
      setStructuredMessages([]);
      setLastReceivedTurnId(null);
      lastReceivedTurnIdRef.current = null;
      lastServerContentHashRef.current = null;
      hasThreadBeenLoadedRef.current = false;
      dispatch(reduxActions.setChatThreadLost(false));
      dispatch(reduxActions.setIsChatHeaderVisible(true));
    }
    previousAssistantRef.current = selectedAssistant;
  }, [selectedAssistant, dispatch]);

  useEffect(() => {
    if (isInitialized && filteredAssistants.length > 0 && selectedAssistant && !threadId && !hasThreadBeenLoadedRef.current) {
      hasThreadBeenLoadedRef.current = true;
      loadOrCreateThread();
      loadAttachments();
    }
  }, [isInitialized, filteredAssistants.length, selectedAssistant, threadId]); // Removed function dependencies

  // Tick co 15 min (TYLKO w trybie WiseAi) — lekkie `/users/profile` zmusza axios
  // interceptor do odswiezenia tokena gdy zblizyl sie do wygasniecia. W trybie Orion
  // refresh wykonuje sie prefetchem przed kazda wiadomoscia (patrz sendMessage).
  useEffect(() => {
    if (!isInitialized || !isWiseAiEnabled()) return;
    const TOKEN_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
    const tick = () => {
      axios.get('/users/profile').catch(() => {
        // Interceptor obsluzy 401 sam; ignorujemy reszte bledow.
      });
    };
    tick();
    const intervalId = window.setInterval(tick, TOKEN_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [isInitialized]);

  useEffect(() => {
    const init = async () => {
      try {
        if (!document.querySelector('script[src*="charts.js"]')) {
          const script = document.createElement('script');
          script.src = '/_content/Hydra.Shared/js/charts.js';
          script.async = true;
          // Ignoruj błąd ładowania skryptu - opcjonalna funkcjonalność
          script.onerror = () => {
            // Skrypt charts.js jest opcjonalny, błąd nie blokuje aplikacji
          };
          document.head.appendChild(script);
        }
      } catch (error) {
        // Ignoruj błąd - skrypt jest opcjonalny i może nie być dostępny
      }

      setIsInitialized(true);
    };

    init();

    const handleVisibilityChange = () => {
      if (!document.hidden && threadId) {
        // Use inline scroll instead of function ref to avoid dependencies
        if (chatDivRef.current) {
          requestAnimationFrame(() => {
            if (chatDivRef.current) {
              chatDivRef.current.scrollTop = chatDivRef.current.scrollHeight;
            }
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [threadId]); // Removed loadHistory and scrollToBottom to prevent memory leak

  useEffect(() => {
    if (isInitialized && !urlParameters?.default_assistant && filteredAssistants.length > 0) {
      const storedAssistant = localStorage.getItem('selectedAssistant');
      if (storedAssistant && !selectedAssistant) {
        const foundAssistant = findAssistantByNameOrSymbol(storedAssistant);
        if (foundAssistant && filteredAssistants.includes(foundAssistant)) {
          setSelectedAssistant(foundAssistant.name);
        }
      }
    }
  }, [isInitialized, filteredAssistants, urlParameters?.default_assistant, selectedAssistant, findAssistantByNameOrSymbol]);

  useEffect(() => {
    if (threadId && isInitialized) {
      updateThreadContextAsync();
    }
  }, [location.pathname, threadId, isInitialized]); // Removed function dependency

  // Parse messages into bubbles - memoized to prevent re-parsing on every render
  const parsedMessages = useMemo(() => {
    return simpleParseMessages(conversationContent, includeDetails);
  }, [conversationContent, includeDetails]);

  return {
    // State
    userMessage,
    setUserMessage,
    threadId,
    conversationContent,
    selectedAssistant,
    isProcessing,
    includeDetails,
    setIncludeDetails,
    attachedFiles,
    setAttachedFiles,
    showFileUpload,
    setShowFileUpload,
    filteredAssistants,
    isIframeFullscreen,
    
    // Refs
    chatDivRef,
    textAreaRef,
    assistantDropdownRef,
    fileInputRef,
    
    // Computed
    isDeveloperMode,
    showAssistantDropdown,
    attachedFilesCount,
    
    // Functions
    sendMessage,
    stopProcessing,
    startNewThread,
    removeLastMessage,
    selectAssistant,
    canCurrentAssistantAttachFiles,
    handleFileChange,
    handleFileRemoved,
    handleToggleFullscreen,
    handleKeyDown,
    loadHistory,
    parsedMessages,
    structuredMessages,
    t
  };
};

